// ============================================================================
// Building time — the AM's view of where a night manager's hours went
// (item #997, Ben 2026-10-27).
//
// Shown on My night managers → one night manager, to the employee the position
// reports to, an executive or a superuser (row security decides the rows; the
// screen only decides whether to show the panel). "The AM can use the numbers
// however they want."
//
// ⛔ HOURS ONLY, AND THEY ADD UP TO THE PUNCHED HOURS TO THE MINUTE. The total
// is the nights' paid minutes; "Driving / between buildings" is what is left
// after the check-ins. Nothing here changes a night, and nothing here appears
// on the client hours page.
// ============================================================================

import { useMemo, useState } from 'react';
import { formatSiteTime } from '../../lib/siteTime';
import { fmtMinutes, summarizeBuildingTime, type BuildingTimeNight, type Stretch } from './building-time';
import { useBuildingVisits, usePayPeriods } from './hooks/use-am-data';
import type { NightRow } from './types-am';

// Token classes only (the palette gate). One per building, in order of hours.
const BUILDING_TONES = [
  'border-st-ok-border bg-st-ok-bg text-st-ok-fg',
  'border-st-info-border bg-st-info-bg text-st-info-fg',
  'border-st-warn-border bg-st-warn-bg text-st-warn-fg',
  'border-tcs-border-strong bg-tcs-inverse text-tcs-inverse-ink',
];
const BETWEEN_TONE = 'border-tcs-border bg-tcs-surface text-tcs-text-muted';

function fmtDay(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function pct(x: number): string {
  return `${Math.round(x * 100)}%`;
}

export function BuildingTimePanel({ workerId, nights, recordsBuildings }: {
  workerId: string;
  nights: NightRow[];
  /** The position's setting today. The panel still shows past check-ins when it is off. */
  recordsBuildings: boolean;
}) {
  const { rows: periods, error: periodError } = usePayPeriods();
  const [picked, setPicked] = useState<string | null>(null);
  const period = periods.find((p) => p.period_start === picked) ?? periods[0] ?? null;
  const { rows: visits, loading, error } = useBuildingVisits(workerId, true);

  const tz = useMemo(() => new Map(nights.map((n) => [n.segment_id, n.site_tz])), [nights]);
  const summary = useMemo(() => {
    if (!period) return null;
    const ns: BuildingTimeNight[] = nights.map((n) => ({
      segment_id: n.segment_id, shift_date: n.shift_date, started_at: n.started_at, ended_at: n.ended_at,
      paid_minutes: n.paid_minutes, segment_status: n.segment_status,
    }));
    return summarizeBuildingTime(ns, visits, period.period_start, period.period_end);
  }, [nights, visits, period]);

  const toneOf = useMemo(() => {
    const m = new Map<string, string>();
    (summary?.buildings ?? []).forEach((b, i) => m.set(b.facility_id, BUILDING_TONES[i % BUILDING_TONES.length]));
    return m;
  }, [summary]);

  if (!recordsBuildings && visits.length === 0 && !loading && !error) {
    return (
      <section className="mt-4 rounded-xl border border-dashed border-tcs-border-strong px-4 py-4">
        <div className="text-sm font-bold text-tcs-text">Building time</div>
        <div className="mt-1 text-xs text-tcs-text-secondary">
          This position is set to <b>Night only</b>, so there are no building check-ins to show. Switch it to
          {' '}<b>Night + buildings</b> on the list of night managers and they check in and out of each building during the night.
        </div>
      </section>
    );
  }

  const stretchTitle = (s: Stretch, segTz: string | null) =>
    `${s.kind === 'building' ? s.place_display : 'Driving / between buildings'} · ${formatSiteTime(s.from, segTz)}–${formatSiteTime(s.to, segTz)} · ${fmtMinutes(s.minutes)}`;

  return (
    <section className="mt-4 rounded-xl border border-tcs-border bg-tcs-raised p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-tcs-text">Building time</div>
          <div className="text-xs text-tcs-text-secondary">
            Where the punched hours went, from their check-ins. It changes no hours and is not shown to the client.
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor={`bt-period-${workerId}`}>Pay period</label>
          <select id={`bt-period-${workerId}`} value={period?.period_start ?? ''} onChange={(e) => setPicked(e.target.value)}
            className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text">
            {periods.map((p) => (
              <option key={p.period_start} value={p.period_start}>{fmtDay(p.period_start)} – {fmtDay(p.period_end)}</option>
            ))}
          </select>
        </div>
      </div>

      {(periodError || error) && <div className="mt-3 text-sm text-st-danger-fg">{periodError ?? error}</div>}
      {loading && <div className="mt-3 text-sm text-tcs-text-muted">Loading…</div>}

      {!loading && !error && summary && (
        <>
          <div className="mt-3 overflow-x-auto rounded-lg border border-tcs-border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                  <th className="px-3 py-2 font-semibold">Building</th>
                  <th className="px-3 py-2 text-right font-semibold">Visits</th>
                  <th className="px-3 py-2 text-right font-semibold">Hours this period</th>
                  <th className="px-3 py-2 text-right font-semibold">Share</th>
                </tr>
              </thead>
              <tbody>
                {summary.buildings.map((b) => (
                  <tr key={b.facility_id} className="border-t border-tcs-border">
                    <td className="px-3 py-2 text-tcs-text">
                      <span className={`mr-2 inline-block h-2.5 w-2.5 rounded-sm border align-middle ${toneOf.get(b.facility_id)}`} />
                      {b.place_display}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{b.visits}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMinutes(b.minutes)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(b.share)}</td>
                  </tr>
                ))}
                <tr className="border-t border-tcs-border">
                  <td className="px-3 py-2 italic text-tcs-text-secondary">
                    <span className={`mr-2 inline-block h-2.5 w-2.5 rounded-sm border align-middle ${BETWEEN_TONE}`} />
                    Driving / between buildings
                  </td>
                  <td className="px-3 py-2 text-right text-tcs-text-muted">—</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMinutes(summary.between_minutes)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{pct(summary.between_share)}</td>
                </tr>
                <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-semibold">
                  <td className="px-3 py-2">Total on the clock</td>
                  <td className="px-3 py-2 text-right tabular-nums">{summary.buildings.reduce((t, b) => t + b.visits, 0)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMinutes(summary.punched_minutes)}</td>
                  <td className="px-3 py-2" />
                </tr>
              </tbody>
            </table>
          </div>

          {summary.nights.length === 0 && (
            <div className="mt-3 text-xs text-tcs-text-muted">No nights in this pay period.</div>
          )}

          {summary.nights.length > 0 && (
            <div className="mt-4 flex flex-col gap-3">
              {summary.nights.map((n) => {
                const segTz = tz.get(n.segment_id) ?? null;
                const total = n.stretches.reduce((t, s) => t + s.minutes, 0) || 1;
                return (
                  <div key={n.segment_id}>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="font-semibold text-tcs-text">{fmtDay(n.shift_date)}</span>
                      <span className="text-tcs-text-secondary">
                        {fmtMinutes(n.visit_minutes)} in buildings · {fmtMinutes(n.between_minutes)} between · {fmtMinutes(n.punched_minutes)} on the clock
                      </span>
                    </div>
                    {n.stretches.length === 0 || n.visit_minutes === 0 ? (
                      <div className="mt-1 rounded-md border border-dashed border-tcs-border px-2 py-1.5 text-[11.5px] text-tcs-text-muted">
                        No building check-ins this night.
                      </div>
                    ) : (
                      <div className="mt-1 flex h-8 overflow-hidden rounded-md border border-tcs-border">
                        {n.stretches.map((s, i) => (
                          <div key={i} title={stretchTitle(s, segTz)}
                            style={{ flexGrow: Math.max(s.minutes, 1) / total, flexBasis: 0 }}
                            className={`flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap border-r px-1 text-[10.5px] last:border-r-0 ${
                              s.kind === 'building' ? toneOf.get(s.facility_id) ?? BUILDING_TONES[0] : BETWEEN_TONE}`}>
                            {s.kind === 'building' ? s.place_display : (s.minutes >= 20 ? 'between' : '')}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
